import { Box, Chip, Tooltip } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import { TRANSFER_STATUS_TO_LABEL } from "../../constants"

import { TransferAs400SyncStatus, TransferStatus } from "@/graphql/codegen/graphql"

const STATUS_TO_CHIP_CLASS: Record<TransferStatus, string> = {
  [TransferStatus.Draft]: "bg-gray-300",
  [TransferStatus.PendingPickAndPutaway]: "bg-tertiary-yellow-300",
  [TransferStatus.Completed]: "bg-tertiary-green-300",
  [TransferStatus.Cancelled]: "bg-secondary-200",
} as const

// AK-5563 AC1, AC8: pending states share one label; failed states are named per failed action.
const AS400_SYNC_STATUS_TO_LABEL: Record<TransferAs400SyncStatus, string> = {
  [TransferAs400SyncStatus.WorkOrderUpserting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderConfirming]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderDeleting]: "Pending Sync to AS400",
  [TransferAs400SyncStatus.WorkOrderUpsertFailed]: "Failed Creation",
  [TransferAs400SyncStatus.WorkOrderConfirmationFailed]: "Failed Completion",
  [TransferAs400SyncStatus.WorkOrderDeletionFailed]: "Failed Cancellation",
} as const

const AS400_SYNC_FAILURE_STATUSES = new Set<TransferAs400SyncStatus>([
  TransferAs400SyncStatus.WorkOrderUpsertFailed,
  TransferAs400SyncStatus.WorkOrderConfirmationFailed,
  TransferAs400SyncStatus.WorkOrderDeletionFailed,
])

interface TransferStatusCellProps {
  status: TransferStatus
  as400SyncStatus?: TransferAs400SyncStatus | null
}

export const TransferStatusCell: FC<TransferStatusCellProps> = ({ status, as400SyncStatus }) => (
  <Box className='flex flex-col items-start gap-2'>
    <Chip
      className={twMerge("h-6 rounded text-xs", STATUS_TO_CHIP_CLASS[status])}
      label={TRANSFER_STATUS_TO_LABEL[status]}
      size='small'
    />
    {as400SyncStatus && (
      <Box className='flex items-center gap-2'>
        <Chip
          className='h-6 rounded bg-secondary-200 text-xs'
          label={AS400_SYNC_STATUS_TO_LABEL[as400SyncStatus]}
          size='small'
        />
        {AS400_SYNC_FAILURE_STATUSES.has(as400SyncStatus) && (
          <Tooltip title='AS400 sync failed'>
            <WarningOutlined className='text-secondary-600' fontSize='small' />
          </Tooltip>
        )}
      </Box>
    )}
  </Box>
)
